// Network half of sync (SPEC.md §7.2): pull from sync_records, push through sync_push(), and decide
// when to run. The device half (applying and collecting rows) lives in db/syncOps.ts.
import { supabase } from '../auth/supabase';
import { withTimeout } from '../account/entitlement';
import type { DbClient } from '../db/client';
import type { SyncRecord } from '../db/syncOps';

const BATCH = 500;
const CURSOR_KEY = 'sync_cursor';
const LAST_SYNC_KEY = 'sync_last_at';
const PUSH_DEBOUNCE_MS = 5_000;
const PERIODIC_MS = 5 * 60_000;
const REQUEST_TIMEOUT_MS = 30_000;

export type SyncPhase = 'idle' | 'syncing' | 'offline' | 'read-only' | 'error';

export interface SyncStatus {
  /** False until the engine has read this device's sync state; the UI waits for it. */
  initialized: boolean;
  phase: SyncPhase;
  /** Rows changed on this device and not yet accepted by the server. */
  pending: number;
  lastSyncedAt: string | null;
  error: string | null;
  /** First download on this device (no cursor yet). `received` counts rows so far. */
  restoring: { received: number } | null;
}

interface PulledRow {
  table_name: string;
  record_id: string;
  hlc: string;
  deleted: boolean;
  data: Record<string, unknown>;
  server_seq: number;
}

class ReadOnlyError extends Error {}

export class SyncEngine {
  private status: SyncStatus = { initialized: false, phase: 'idle', pending: 0, lastSyncedAt: null, error: null, restoring: null };
  private readonly listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private rerun = false;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  constructor(
    private readonly db: DbClient,
    private readonly userId: string
  ) {}

  getStatus = () => this.status;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private set(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  /** Starts automatic syncing. Returns a function that stops it. */
  start(): () => void {
    // React StrictMode starts, stops and restarts effects in development.
    this.stopped = false;
    void (async () => {
      const lastSyncedAt = await this.db.getDeviceSetting(LAST_SYNC_KEY);
      const restoring = (await this.db.getDeviceSetting(CURSOR_KEY)) === null ? { received: 0 } : null;
      this.set({ initialized: true, lastSyncedAt, restoring, pending: await this.db.pendingChanges() });
      void this.syncNow();
    })();

    const offChange = this.db.onTablesChanged((_tables, source) => {
      if (source !== 'local') return;
      void this.refreshPending();
      if (this.debounce) clearTimeout(this.debounce);
      this.debounce = setTimeout(() => void this.syncNow(), PUSH_DEBOUNCE_MS);
    });
    const onOnline = () => void this.syncNow();
    const onOffline = () => this.set({ phase: 'offline' });
    const onVisible = () => {
      if (document.visibilityState === 'visible') void this.syncNow();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    const periodic = setInterval(() => {
      if (document.visibilityState === 'visible') void this.syncNow();
    }, PERIODIC_MS);

    return () => {
      this.stopped = true;
      offChange();
      if (this.debounce) clearTimeout(this.debounce);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(periodic);
    };
  }

  /** Runs a sync now; if one is already running, runs another right after it. */
  syncNow(): Promise<void> {
    if (this.running) {
      this.rerun = true;
      return this.running;
    }
    this.running = (async () => {
      do {
        this.rerun = false;
        await this.runOnce();
      } while (this.rerun && !this.stopped);
    })().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async refreshPending() {
    if (!this.stopped) this.set({ pending: await this.db.pendingChanges() });
  }

  private async runOnce() {
    if (this.stopped) return;
    if (!navigator.onLine) {
      this.set({ phase: 'offline' });
      return;
    }
    this.set({ phase: 'syncing', error: null });
    try {
      // Pull first so a new device gets the account's data before anything else.
      await this.pull();
      // The account's data is on the device now; the app can show while changes upload.
      if (this.status.restoring) this.set({ restoring: null });
      await this.push();
      await this.syncAttachmentFiles();
      const now = new Date().toISOString();
      await this.db.setDeviceSetting(LAST_SYNC_KEY, now);
      this.set({ phase: 'idle', lastSyncedAt: now, restoring: null });
    } catch (error) {
      if (this.stopped) return;
      if (error instanceof ReadOnlyError) this.set({ phase: 'read-only', restoring: null });
      else if (!navigator.onLine) this.set({ phase: 'offline' });
      else this.set({ phase: 'error', error: error instanceof Error ? error.message : String(error) });
    } finally {
      await this.refreshPending().catch(() => undefined);
    }
  }

  private async pull() {
    for (;;) {
      if (this.stopped) return;
      const cursor = Number((await this.db.getDeviceSetting(CURSOR_KEY)) ?? 0);
      const { data, error } = await withTimeout(
        supabase
          .from('sync_records')
          .select('table_name, record_id, hlc, deleted, data, server_seq')
          .gt('server_seq', cursor)
          .order('server_seq')
          .limit(BATCH),
        REQUEST_TIMEOUT_MS
      );
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as PulledRow[];
      if (rows.length) {
        const records: SyncRecord[] = rows.map((row) => ({ t: row.table_name, id: row.record_id, hlc: row.hlc, deleted: row.deleted, data: row.data }));
        await this.db.applyRemote(records);
        await this.db.setDeviceSetting(CURSOR_KEY, String(rows[rows.length - 1].server_seq));
        if (this.status.restoring) this.set({ restoring: { received: this.status.restoring.received + rows.length } });
      } else if (cursor === 0) {
        // An empty account: record the cursor so this device no longer counts as restoring.
        await this.db.setDeviceSetting(CURSOR_KEY, '0');
      }
      if (rows.length < BATCH) return;
    }
  }

  private async push() {
    for (;;) {
      if (this.stopped) return;
      const records = await this.db.collectDirty(BATCH);
      if (!records.length) return;
      const { error } = await withTimeout(supabase.rpc('sync_push', { records }), REQUEST_TIMEOUT_MS);
      if (error) {
        if (error.hint === 'READ_ONLY') throw new ReadOnlyError(error.message);
        throw new Error(error.message);
      }
      // Rejected rows are cleared too: the server's newer version arrives on the next pull.
      await this.db.markPushed(records.map(({ t, id, hlc }) => ({ t, id, hlc })));
      if (records.length < BATCH) return;
    }
  }

  /** Uploads photos whose rows have synced, and removes deleted ones from the account (SPEC.md §7.2). */
  private async syncAttachmentFiles() {
    const work = await this.db.attachmentWork();
    const bucket = supabase.storage.from(ATTACHMENT_BUCKET);
    for (const upload of work.uploads) {
      if (this.stopped) return;
      const bytes = await this.db.readAttachment(upload.id);
      // The file is gone from this device (storage cleared); nothing can be uploaded.
      if (!bytes) continue;
      const { error } = await withTimeout(
        bucket.upload(attachmentKey(this.userId, upload.id), new Blob([bytes], { type: upload.mime }), { contentType: upload.mime, upsert: true }),
        REQUEST_TIMEOUT_MS
      );
      if (error) throw new Error(error.message);
      await this.db.attachmentUploaded(upload.id);
    }
    const remote = work.removals.filter((removal) => removal.uploaded);
    if (remote.length) {
      const { error } = await withTimeout(bucket.remove(remote.map((removal) => attachmentKey(this.userId, removal.id))), REQUEST_TIMEOUT_MS);
      if (error) throw new Error(error.message);
    }
    for (const removal of work.removals) await this.db.attachmentRemoved(removal.id);
  }
}

export const ATTACHMENT_BUCKET = 'attachments';
export const attachmentKey = (userId: string, attachmentId: string) => `${userId}/${attachmentId}`;

/**
 * An attachment's bytes: from this device, or downloaded from the account the first time it is
 * viewed on a device that didn't add it. Null when it isn't here and can't be fetched (offline, demo).
 */
export async function loadAttachment(db: DbClient, userId: string | null, attachmentId: string): Promise<Uint8Array<ArrayBuffer> | null> {
  const local = await db.readAttachment(attachmentId);
  if (local || !userId || !navigator.onLine) return local;
  const { data, error } = await withTimeout(supabase.storage.from(ATTACHMENT_BUCKET).download(attachmentKey(userId, attachmentId)), REQUEST_TIMEOUT_MS);
  if (error || !data) return null;
  const bytes = new Uint8Array(await data.arrayBuffer());
  await db.storeAttachment(attachmentId, bytes);
  return bytes;
}
